import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { createUserPrivateClub, getUserAggregatedInfo, getUsersPaginated } from '../Api';
import AccountMainInfoDto from '../models/dtos/AccountMainInfoDto';
import PrivateClubDto, { PrivateClubRewardMode, PrivateClubTemplateDto } from '../models/dtos/PrivateClubDto';

const MIN_ENTRY_FEE_BETCOINS = 1_000;
const MAX_ENTRY_FEE_BETCOINS = 1_000_000_000;

type CreatePrivateClubDialogProps = {
    open: boolean;
    templates: PrivateClubTemplateDto[];
    initialUser?: AccountMainInfoDto | null;
    initialTemplateId?: string | null;
    onClose: () => void;
    onCreated: (club: PrivateClubDto) => void | Promise<void>;
};

function userName(user: AccountMainInfoDto) {
    return user.name?.trim() || user.email?.trim() || `User ${user.id}`;
}

function userLabel(user: AccountMainInfoDto) {
    return `${userName(user)} - ID ${user.id}`;
}

function mergeUsers(users: AccountMainInfoDto[], selectedUser: AccountMainInfoDto | null) {
    const merged = selectedUser ? [selectedUser, ...users] : users;
    return Array.from(new Map(merged.map(user => [user.id, user])).values());
}

export default function CreatePrivateClubDialog({
    open,
    templates,
    initialUser = null,
    initialTemplateId = null,
    onClose,
    onCreated,
}: CreatePrivateClubDialogProps) {
    const [selectedUser, setSelectedUser] = React.useState<AccountMainInfoDto | null>(null);
    const [userOptions, setUserOptions] = React.useState<AccountMainInfoDto[]>([]);
    const [userSearch, setUserSearch] = React.useState('');
    const [usersLoading, setUsersLoading] = React.useState(false);
    const [templateId, setTemplateId] = React.useState('');
    const [name, setName] = React.useState('');
    const [avatar, setAvatar] = React.useState('');
    const [rulesText, setRulesText] = React.useState('');
    const [rewardMode, setRewardMode] = React.useState<PrivateClubRewardMode>('SOCIAL');
    const [entryFeeBetcoins, setEntryFeeBetcoins] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!open) return;
        const firstAvailableTemplate = templates.find(template => template.status === 'ACTIVE') ?? templates[0];
        const requestedTemplate = templates.find(template => template.id === initialTemplateId);
        setSelectedUser(initialUser);
        setUserOptions(initialUser ? [initialUser] : []);
        setUserSearch('');
        setTemplateId(requestedTemplate?.id ?? firstAvailableTemplate?.id ?? '');
        setName('');
        setAvatar('');
        setRulesText('');
        setRewardMode('SOCIAL');
        setEntryFeeBetcoins('');
        setSubmitting(false);
        setError(null);
    }, [initialTemplateId, initialUser, open, templates]);

    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const timeoutId = window.setTimeout(async () => {
            setUsersLoading(true);
            const searchTerm = userSearch.trim();
            const exactUserId = /^\d+$/.test(searchTerm) ? Number(searchTerm) : null;
            const [response, exactUserResponse] = await Promise.all([
                getUsersPaginated(0, 20, searchTerm || undefined),
                exactUserId != null && exactUserId > 0
                    ? getUserAggregatedInfo(exactUserId)
                    : Promise.resolve(null),
            ]);
            if (!cancelled) {
                const users = exactUserResponse?.general
                    ? [exactUserResponse.general, ...(response?.items ?? [])]
                    : response?.items ?? [];
                setUserOptions(mergeUsers(users, selectedUser));
                setUsersLoading(false);
            }
        }, 300);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [open, selectedUser, userSearch]);

    const createClub = async () => {
        setError(null);
        if (!selectedUser) {
            setError('User is required.');
            return;
        }
        if (!templateId) {
            setError('Template is required.');
            return;
        }
        if (!name.trim()) {
            setError('Club name is required.');
            return;
        }
        const selectedTemplate = templates.find(template => template.id === templateId);
        if (selectedTemplate?.status !== 'ACTIVE') {
            setError('A club can only be created from an active template.');
            return;
        }
        let entryFee: number | null = null;
        if (rewardMode === 'BETCOIN') {
            entryFee = Number(entryFeeBetcoins);
            if (!entryFeeBetcoins.trim() || Number.isNaN(entryFee)) {
                setError('Entry fee is required for betcoin clubs.');
                return;
            }
            if (entryFee < MIN_ENTRY_FEE_BETCOINS || entryFee > MAX_ENTRY_FEE_BETCOINS) {
                setError(`Entry fee must be between ${MIN_ENTRY_FEE_BETCOINS.toLocaleString()} and ${MAX_ENTRY_FEE_BETCOINS.toLocaleString()} betcoins.`);
                return;
            }
        }

        setSubmitting(true);
        const result = await createUserPrivateClub(selectedUser.id, {
            templateId,
            name: name.trim(),
            avatar: avatar.trim() || null,
            rulesText: rulesText.trim() || null,
            rewardMode,
            entryFeeBetcoins: rewardMode === 'BETCOIN' ? entryFee : null,
        });
        setSubmitting(false);
        if (!result.ok || !result.data) {
            setError(result.error ?? 'Private club was not created.');
            return;
        }

        await onCreated(result.data);
        onClose();
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Create private club</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Autocomplete
                        options={userOptions}
                        value={selectedUser}
                        loading={usersLoading}
                        filterOptions={options => options}
                        getOptionLabel={userLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input' || reason === 'clear') setUserSearch(value);
                        }}
                        onChange={(_, value) => setSelectedUser(value)}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar src={option.photoUrl} sx={{ width: 32, height: 32 }} />
                                    <Box>
                                        <Typography variant="body2">{userName(option)}</Typography>
                                        <Typography variant="caption" color="text.secondary">ID {option.id}</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="User"
                                placeholder="Search by name, email, or user ID"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {usersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Template</InputLabel>
                        <Select
                            label="Template"
                            value={templateId}
                            onChange={event => setTemplateId(event.target.value)}
                            renderValue={value => {
                                const template = templates.find(item => item.id === value);
                                return template ? `${template.title} - ID ${template.id}` : value;
                            }}
                        >
                            {templates.map(template => (
                                <MenuItem key={template.id} value={template.id} disabled={template.status !== 'ACTIVE'}>
                                    <Stack spacing={0.25}>
                                        <Typography variant="body2">{template.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ID {template.id} - {template.status}
                                        </Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField label="Club name" value={name} onChange={event => setName(event.target.value)} fullWidth />
                    <TextField label="Avatar URL" value={avatar} onChange={event => setAvatar(event.target.value)} fullWidth />
                    <TextField
                        label="Rules"
                        value={rulesText}
                        onChange={event => setRulesText(event.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Reward mode</InputLabel>
                        <Select
                            label="Reward mode"
                            value={rewardMode}
                            onChange={event => setRewardMode(event.target.value as PrivateClubRewardMode)}
                        >
                            <MenuItem value="SOCIAL">Social - rewards handled outside the app</MenuItem>
                            <MenuItem value="BETCOIN">Betcoin - members pay an entry fee, winners split the bank</MenuItem>
                        </Select>
                    </FormControl>
                    {rewardMode === 'BETCOIN' && (
                        <TextField
                            label="Entry fee (betcoins)"
                            type="number"
                            value={entryFeeBetcoins}
                            onChange={event => setEntryFeeBetcoins(event.target.value)}
                            helperText={`Between ${MIN_ENTRY_FEE_BETCOINS.toLocaleString()} and ${MAX_ENTRY_FEE_BETCOINS.toLocaleString()} betcoins`}
                            fullWidth
                            inputProps={{ min: MIN_ENTRY_FEE_BETCOINS, max: MAX_ENTRY_FEE_BETCOINS, step: 1 }}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={createClub}
                    disabled={submitting || !selectedUser || !templateId || templates.length === 0}
                >
                    {submitting ? 'Creating...' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
